import React, { useState } from "react";
import { api } from "../../api";

/* Parse CPT/ICD the same way the guidelines dialog does. */
const parseCpt = (codeStr, descStr) => {
  if (!codeStr) return [];
  const codes = codeStr.split(", "); const descs = (descStr || "").split("; ");
  return codes.map((c, i) => { const p = c.split("-"); const cp = (p[0] || "").split("*"); return { code: (cp[0] || "").trim(), units: (cp[1] || "").trim(), modifier: (p[1] || "").trim(), desc: (descs[i] || "").trim() }; }).filter((x) => x.code);
};
const parseIcd = (codeStr, descStr) => {
  if (!codeStr) return [];
  const codes = codeStr.split(", "); const descs = (descStr || "").split("; ");
  return codes.map((c, i) => ({ code: c.trim(), desc: (descs[i] || "").trim() })).filter((x) => x.code);
};
const clean = (s) => { const t = (s == null ? "" : String(s)).trim(); return (!t || ["NA", "N/A"].includes(t.toUpperCase())) ? "" : t; };

export default function GuidelineLookup() {
  const [code, setCode] = useState("");
  const [g, setG] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const find = () => {
    const q = code.trim();
    if (!q) return;
    setLoading(true); setNotFound(false); setG(null);
    api.guidelineByCode(q)
      .then((d) => { if (d && (d.id || d.code)) setG(d); else setNotFound(true); })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  };
  const clear = () => { setCode(""); setG(null); setNotFound(false); setLoading(false); };

  const crumb = g ? [g.clientName, g.specialtyName, g.orderTypeName, g.orderName, g.anatomicalLocationName, g.techniqueName].filter(clean).join("  ›  ") : "";
  const cpts = g ? parseCpt(g.cptCode, g.cptDescription) : [];
  const icds = g ? parseIcd(g.icdCode, g.icdDescription) : [];
  const sections = g ? [
    ["generalGuidelines", "General"], ["payorGuidelines", "Payor"],
    ["facilityGuidelines", "Test Facility"], ["locationGuidelines", "Location / Clinic"], ["lcdGuidelines", "LCD"],
  ].filter(([k]) => clean(g[k])) : [];

  const th = { textAlign: "left", fontSize: 11, textTransform: "uppercase", letterSpacing: ".03em", color: "#6b7280", padding: "6px 9px", background: "#f8fafc", borderBottom: "1px solid #e5e7eb" };
  const td = { padding: "6px 9px", borderBottom: "1px solid #eef2f7", fontSize: 13.5, verticalAlign: "top" };

  return (
    <section className="card" style={{ maxWidth: 960 }}>
      <h2 style={{ margin: "0 0 4px", color: "#1e3a5f" }}>Guideline Lookup</h2>
      <div style={{ color: "#64748b", fontSize: 13.5, marginBottom: 14 }}>Enter the 7-series Guideline ID (e.g. 70042) a worker referenced to view the full guideline.</div>

      <div style={{ display: "flex", gap: 8, marginBottom: 18, maxWidth: 420, flexWrap: "wrap" }}>
        <input className="inp" value={code} placeholder="e.g. 70042" style={{ flex: 1, minWidth: 160, fontFamily: "ui-monospace,Menlo,Consolas,monospace", letterSpacing: ".08em" }}
          onChange={(e) => setCode(e.target.value)} onKeyDown={(e) => e.key === "Enter" && find()} />
        <button className="btn btn-primary" onClick={find} disabled={loading}>{loading ? "…" : "Find"}</button>
        <button className="btn btn-ghost" onClick={clear}>Clear</button>
      </div>

      {notFound && <div style={{ color: "#b45309", fontWeight: 700 }}>No guideline found for “{code.trim()}”.</div>}

      {g && (
        <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: 18 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 8 }}>
            <span style={{ fontWeight: 800, color: "#1e5a8e", background: "#eaf3fb", border: "1px solid #d7dbea", borderRadius: 8, padding: "4px 12px", fontFamily: "ui-monospace,Menlo,Consolas,monospace" }}>ID {g.code || "—"}</span>
            {g.status === "APPROVED"
              ? <span style={{ color: "#16a34a", fontWeight: 800 }}>✓ Approved{g.approvedByName ? ` · ${g.approvedByName}` : ""}</span>
              : <span style={{ color: "#d97706", fontWeight: 800 }}>● {g.status || "Pending"}</span>}
          </div>
          <div style={{ fontWeight: 700, color: "#1e5a8e", marginBottom: 14, lineHeight: 1.5 }}>{crumb}</div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 16 }}>
            <div>
              <div style={{ fontWeight: 800, fontSize: 12, color: "#2f80c4", marginBottom: 5 }}>CPT</div>
              <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden" }}>
                <thead><tr><th style={th}>Code</th><th style={th}>Units</th><th style={th}>Description</th></tr></thead>
                <tbody>
                  {cpts.length ? cpts.map((r, i) => (
                    <tr key={i}><td style={{ ...td, fontWeight: 700, whiteSpace: "nowrap" }}>{r.code}{r.modifier ? ` -${r.modifier}` : ""}</td><td style={td}>{r.units && r.units !== "0" ? r.units : "—"}</td><td style={td}>{r.desc || "—"}</td></tr>
                  )) : <tr><td style={{ ...td, color: "#9aa0bc", fontStyle: "italic" }} colSpan={3}>No CPT codes</td></tr>}
                </tbody>
              </table>
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 12, color: "#0E7C66", marginBottom: 5 }}>ICD-10</div>
              <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden" }}>
                <thead><tr><th style={th}>Code</th><th style={th}>Description</th></tr></thead>
                <tbody>
                  {icds.length ? icds.map((r, i) => (
                    <tr key={i}><td style={{ ...td, fontWeight: 700, whiteSpace: "nowrap" }}>{r.code}</td><td style={td}>{r.desc || "—"}</td></tr>
                  )) : <tr><td style={{ ...td, color: "#9aa0bc", fontStyle: "italic" }} colSpan={2}>No ICD-10 codes</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          {sections.map(([k, label]) => (
            <div key={k} style={{ marginTop: 16 }}>
              <div style={{ fontWeight: 800, fontSize: 12, color: "#1e5a8e", textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 5 }}>{label}</div>
              <div style={{ fontSize: 13.5, lineHeight: 1.6, color: "#334155" }} dangerouslySetInnerHTML={{ __html: g[k] }} />
            </div>
          ))}
        </div>
      )}
    </section>
  );
}